import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, CellStyle, CellClassParams, ValueFormatterFunc, ValueParserFunc } from 'ag-grid-community';
import type { CellValueChangedEvent, CellEditingStoppedEvent } from 'ag-grid-community/dist/types/core/events';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { useCurrentAppMonth } from '../../hooks/useCurrentAppMonth';
import { useBudgetContext, useBudgetWindow } from '../../store/Budget/BudgetContext';
import { generateMonthColumns, getVisibleMonths, getWindowMonths, MONTH_KEY } from './helpers';
import { buildIdentifierRow, getIdentifierColumns } from './identifierColumns';
import { buildExtrapolatedRow } from './budgetCalculation';
import { ABT_OVERRIDE_CELL_BG, pendingBorder } from './pendingCellStyle';

interface UseRateGridParams {
    billingConfigs: BillingConfigWithRelations[];
    // Which monthly series this grid edits — also the field the batch payload carries it in.
    editKind: 'minutePriceEdits' | 'contractABTEdits';
    // The page's pending store (CALL-779). This grid reads it to paint and writes to it on edit; it
    // never persists anything itself.
    pending: PendingConfigEdits;
    minWidth: number;
    valueFormatter: ValueFormatterFunc;
    valueParser: ValueParserFunc;
    // The entered monthly rates for one config (month -> value), entered months only.
    getEnteredMap: (config: BillingConfigWithRelations) => Map<string, number>;
    // Optional horizon anchor for the forward fill — the Vertrags-ABT grid passes the minute-price
    // map so its extrapolation is bounded by the price horizon (the cost path gates ABT on prices, so
    // the ABT shown matches what's charged). Defaults to the entered map for the Minutenpreise grid.
    getAnchorMap?: (config: BillingConfigWithRelations) => Map<string, number>;
    /** CALL-781: hide month COLUMNS before the current month. Display only — see `months` below. */
    hidePastMonths?: boolean;
    // CALL-822: mark ENTERED cells whose value differs from a per-config reference — the ABT grid's
    // Standard-ABT, which a monthly entry silently overrides in the cost calculation. Entered cells
    // only: an extrapolated cell is a projection OF the entered ones, already painted blue. No
    // reference (null/0) means nothing to diverge from, so nothing is marked.
    divergence?: {
        getReference: (config: BillingConfigWithRelations) => number | null | undefined;
        getTooltip: (reference: number) => string;
    };
}

interface RateGrid {
    gridRef: React.RefObject<AgGridReact>;
    rowData: Record<string, unknown>[];
    columnDefs: ColDef[];
    defaultColDef: ColDef;
    onCellValueChanged: (event: CellValueChangedEvent) => void;
    onCellEditingStopped: (event: CellEditingStoppedEvent) => void;
}

/**
 * CALL-752: shared controller for the two Budget rate-entry grids (Minutenpreise, Vertrags-ABT),
 * which are identical apart from the rate field, formatting, column width, and the ABT grid's
 * price-horizon anchor. Owns the AG Grid wiring: month columns with the blue-extrapolated /
 * yellow-entered cell style, forward-extrapolated row data, and the force-repaint effect (AG Grid skips
 * re-running cellStyle when only the entered/extrapolated FLAG changes, not the value).
 *
 * CALL-779: an edit no longer saves. It goes into the page's pending store and the page's one Save posts
 * every grid's changes together — which also retires the bookkeeping per-cell saving needed: rebuilding
 * a config's whole entered set on every keystroke, and clearing the just-committed cell's stale
 * `extrapolated_` flag first so that rebuild would not drop it as display-only.
 */
export const useRateGrid = ({
    billingConfigs,
    editKind,
    pending,
    minWidth,
    valueFormatter,
    valueParser,
    getEnteredMap,
    getAnchorMap,
    hidePastMonths = false,
    divergence,
}: UseRateGridParams): RateGrid => {
    const gridRef = useRef<AgGridReact>(null);
    const { t } = useTranslation('budget');
    const { canEdit } = useBudgetContext();
    const { windowStart } = useBudgetWindow();

    // CALL-781: `months` stays the FULL window — buildExtrapolatedRow below builds the row's values over
    // it, so narrowing it here would drop the past months' figures from the row itself rather than just
    // from the view. Only the column list narrows.
    const currentMonth = useCurrentAppMonth();
    const months = useMemo(() => getWindowMonths(windowStart), [windowStart]);
    const visibleMonths = useMemo(
        () => getVisibleMonths(months, hidePastMonths, currentMonth),
        [months, hidePastMonths, currentMonth]
    );

    // What the server last served, per config — the row build's base, and the pristine value an edit is
    // measured against, so typing a rate back to its stored value drops out of the pending set instead
    // of counting as a change.
    const servedByConfig = useMemo(
        () => new Map(billingConfigs.map((config) => [config.id, getEnteredMap(config)])),
        [billingConfigs, getEnteredMap]
    );

    // This grid's slice of the pending set, indexed for both the row build and cellStyle: keyed by config
    // so a config's edits are found without scanning, and holding EVERY pending month rather than only
    // the visible ones — the month window can be scrolled while edits are outstanding.
    const pendingByConfig = useMemo(() => {
        const byConfig = new Map<number, Map<string, number | null>>();

        for (const edit of pending.edits) {
            if (edit.kind !== editKind) continue;
            const forConfig = byConfig.get(edit.rowId) ?? new Map<string, number | null>();
            forConfig.set(edit.key, edit.value as number | null);
            byConfig.set(edit.rowId, forConfig);
        }

        return byConfig;
    }, [pending.edits, editKind]);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(visibleMonths, {
                minWidth,
                editable: canEdit,
                useValueFormatterForExport: false,
                valueFormatter,
                valueParser,
                // CALL-752: extrapolated future cells are light blue (#e3f2fd), like Strat FC; entered
                // editable cells stay yellow. Both remain editable so the user can override.
                // CALL-779: an unsaved cell keeps its background and gains a red border, so a pending
                // edit is legible on either of them.
                cellStyle: (params: CellClassParams): CellStyle => {
                    const field = params.colDef?.field;
                    const modified = pendingBorder(
                        Boolean(
                            field && params.data?.id != null && pendingByConfig.get(Number(params.data.id))?.has(field)
                        )
                    );

                    if (field && params.data?.[`extrapolated_${field}`] === true) {
                        return { backgroundColor: '#e3f2fd', textAlign: 'right', ...modified };
                    }
                    // CALL-822: painted in read mode too — the override is exactly what a reviewer who
                    // cannot edit needs to see.
                    if (field && params.data?.[`diverges_${field}`] === true) {
                        return { backgroundColor: ABT_OVERRIDE_CELL_BG, textAlign: 'right', ...modified };
                    }
                    return canEdit
                        ? { backgroundColor: '#fffde7', textAlign: 'right', ...modified }
                        : { textAlign: 'right', ...modified };
                },
                tooltipValueGetter: divergence
                    ? (params) => {
                          const field = params.colDef && 'field' in params.colDef ? params.colDef.field : undefined;
                          const reference = params.data?.__divergenceReference;
                          return field && params.data?.[`diverges_${field}`] === true && reference != null
                              ? divergence.getTooltip(Number(reference))
                              : null;
                      }
                    : undefined,
            }),
        [visibleMonths, canEdit, minWidth, valueFormatter, valueParser, pendingByConfig, divergence]
    );

    const rowData = useMemo(
        () =>
            billingConfigs.map((config) => {
                const row: Record<string, unknown> = {
                    id: config.id,
                    ...buildIdentifierRow(config),
                };

                // CALL-779: pending edits are layered onto the SERVED values before anything else reads
                // them. Left in ag-grid's own copy of the row they would be lost the moment a background
                // refetch handed the grid new rowData (immutable data + getRowId), and folding them in
                // here is also what makes an unsaved rate behave like a real entry: the cell stops being
                // a projection and the months after it extrapolate from the new number.
                const entered = new Map(servedByConfig.get(config.id) ?? []);
                const pendingMonths = pendingByConfig.get(config.id);
                if (pendingMonths) {
                    for (const [month, value] of pendingMonths) {
                        if (value === null) entered.delete(month);
                        else entered.set(month, Number(value));
                    }
                }

                // CALL-752: fill empty future cells with the forward-extrapolated rate (display only),
                // flagging them so cellStyle paints them blue. Bounded to the same 6-month horizon as
                // the cost/export path, so the grid shows exactly what the budget charges.
                //
                // The ABT grid's anchor deliberately stays on served prices: it only bounds the
                // projection's horizon, so an unsaved price in a later month widens that grid's blue
                // band on Save rather than on the keystroke.
                const anchorMap = getAnchorMap ? getAnchorMap(config) : entered;
                const { values, extrapolatedMonths } = buildExtrapolatedRow(entered, months, anchorMap);
                for (const [month, value] of values) row[month] = value;
                for (const month of extrapolatedMonths) row[`extrapolated_${month}`] = true;

                // CALL-822: flag entered months that override the reference value. Computed AFTER the
                // pending layering above, so typing the value back to the reference clears the mark on
                // the keystroke, not on the save.
                // "Has a reference", not "> 0": a typed 0 Standard-Minutenpreis is a real default (CALL-824).
                const reference = divergence ? divergence.getReference(config) : null;
                if (reference != null) {
                    row.__divergenceReference = reference;
                    for (const [month, value] of entered) {
                        if (value !== reference) row[`diverges_${month}`] = true;
                    }
                }

                return row;
            }),
        [billingConfigs, months, servedByConfig, pendingByConfig, getAnchorMap, divergence]
    );

    // CALL-752: AG Grid skips re-running cellStyle for a cell whose value is unchanged. A cell can
    // flip between entered (yellow) and extrapolated (blue) without its value changing — e.g. after
    // a save/refetch — which would otherwise leave a stale background. Force a repaint on data change.
    // CALL-779: discarding is the same shape of change — the red border has to go while the value
    // returns to what it already was — so the repaint follows the pending set too.
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowData, pendingByConfig]);

    const recordEdit = useCallback(
        (data: Record<string, any>, monthKey: string) => {
            const configId = Number(data.id);
            // The value has already been through the column's valueParser, so it is a number or null
            // (a cleared cell) — never the raw German string a paste delivers.
            const value = data[monthKey] == null ? null : Number(data[monthKey]);
            pending.set(configId, editKind, monthKey, value, servedByConfig.get(configId)?.get(monthKey));
        },
        [pending, editKind, servedByConfig]
    );

    const onCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;
            if (!data?.id || !colDef.field || !MONTH_KEY.test(colDef.field)) return;
            recordEdit(data, colDef.field);
        },
        [recordEdit]
    );

    // CALL-752: a blue cell's editor pre-fills with its projected value, so "open + Enter" (accepting
    // the projection as-is) leaves the value unchanged — and ag-grid v32 short-circuits the save before
    // onCellValueChanged ever fires (saveNewValue returns early when newValue === oldValue). Catch that
    // here: editing stopped with NO change, NOT cancelled (Esc/clear yields newValue == null), on a
    // display-only extrapolated cell → commit the projected value as a real entry.
    const onCellEditingStopped = useCallback(
        (event: CellEditingStoppedEvent) => {
            if (event.valueChanged) return; // a genuine change already went through onCellValueChanged
            if (event.newValue == null) return; // Esc / cleared — not an "accept as-is"
            const { data, colDef } = event;
            const field = colDef.field;
            if (!data?.id || !field || !MONTH_KEY.test(field)) return;
            if (data[`extrapolated_${field}`] !== true) return; // only the accept-projection case
            recordEdit(data, field);
        },
        [recordEdit]
    );

    const columnDefs = useMemo(() => [...getIdentifierColumns(t), ...monthColumns], [monthColumns, t]);

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    return { gridRef, rowData, columnDefs, defaultColDef, onCellValueChanged, onCellEditingStopped };
};
